import { Platform, ViewStyle } from 'react-native';
import { colors } from './colors';

/**
 * 4pt spacing scale. Screen gutters in the mockups measure ~20pt, cards use
 * 14–16pt internal padding, and stacked sections are separated by 20–24pt.
 */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
} as const;

/**
 * Horizontal gutter used by every full-width screen, sheet and bar.
 *
 * 12, the inset the cart was already drawn to. The app reads as a dense
 * catalogue: every page carries three-part rows - address strips, price lines,
 * steppers - and the wider gutter was costing them the width they needed more
 * than the page needed the air. It is also the one number that must not vary:
 * a header at 16 over a list at 12 makes the whole screen look misaligned even
 * though nothing is. Every edge in the app reads this. Change it here and the
 * app follows; never hard-code a page gutter beside it.
 */
export const screenPadding = spacing.md;

/**
 * Corner radii. Minimal - a softened corner, not a rounded one.
 *
 * Halved from where these started (8/12/16/20). At those values every surface
 * in a dense catalogue read as a separate rounded object, and a card, the tile
 * inside it and the chip inside that were three different curves competing on
 * one row. A slight radius still says "this is a surface" without the whole
 * page looking upholstered.
 *
 * `pill` is untouched: a chip or a bar is round because its shape is what
 * identifies it, and squaring those turns them into unlabelled boxes. Circles
 * - avatars, dots, icon discs - set `borderRadius` to half their own size at
 * the call site and do not read this scale at all.
 */
export const radii = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 10,
  pill: 999,
} as const;

/** Fixed control heights, so buttons and inputs line up. */
export const controlHeights = {
  sm: 36,
  md: 44,
  lg: 52,
} as const;

/** Hairline border shared by every outlined surface. */
export const hairline = {
  borderWidth: 1,
  borderColor: colors.border,
} as const satisfies ViewStyle;

/**
 * Elevation. The design is nearly flat - shadows are soft and low-contrast,
 * used only to lift floating cards off the canvas.
 */
export const shadows = {
  none: {},
  sm: Platform.select({
    ios: {
      shadowColor: colors.shadow,
      shadowOpacity: 0.05,
      shadowRadius: 6,
      shadowOffset: { width: 0, height: 2 },
    },
    android: { elevation: 1 },
    default: {},
  }),
  md: Platform.select({
    ios: {
      shadowColor: colors.shadow,
      shadowOpacity: 0.08,
      shadowRadius: 14,
      shadowOffset: { width: 0, height: 6 },
    },
    android: { elevation: 4 },
    default: {},
  }),
} as const;

export type Spacing = keyof typeof spacing;
export type Radius = keyof typeof radii;
